import { isReminderActive } from './components/ReminderPills/ReminderPills';
import {
    deriveDueDate,
    LAUNCH_MILESTONE_BY_TYPE,
    ORPHAN_MILESTONE_DEFINITIONS,
} from './milestones';

import type { MilestoneReminderValues } from './components/ReminderPills/ReminderPills';
import type { OrphanMilestoneDefinition } from './milestones';
import type { RemindersById } from './useHydrateDeadlineValues';
import type { DeadlineMilestoneValues } from './validation';
import type { useGetCycleEvaluation } from '@/api/cycle-evaluation-services/cycle-evaluation';
import type { EvaluationTypeValue } from '@/constants/cycle-evaluation/CycleEvaluation';
import type { FrequencyValue } from '@/constants/Frequency';
import type { CycleEvaluationGroup } from '@/types/cycle-evaluation/CycleEvaluationGroup';
import type { CycleMilestone } from '@/types/cycle-evaluation/CycleMilestone';
import type { ScheduleReminder } from '@/types/cycle-evaluation/CycleSchedule';

import { OFFERED_REMINDER_DAYS_BEFORE_DEADLINE } from '@/utils/schedule';

const sameInstant = (a?: Date, b?: Date): boolean =>
    (a?.getTime() ?? null) === (b?.getTime() ?? null);

// No entry at all means the type never hydrated — unknown, NOT "the administrator unticked
// everything". Read as empty, it would report a difference against every stored reminder and the
// next write would erase them without a gesture from anyone.
const sameReminders = ({
    reminders,
    scheduleReminders,
}: {
    reminders?: MilestoneReminderValues;
    scheduleReminders: ScheduleReminder[];
}): boolean =>
    !reminders ||
    OFFERED_REMINDER_DAYS_BEFORE_DEADLINE.every(
        (daysBeforeDeadline) =>
            isReminderActive({ reminders, daysBeforeDeadline }) ===
            scheduleReminders.some(
                (reminder) => reminder.daysBeforeDeadline === daysBeforeDeadline,
            ),
    );

const getChangedOrphanMilestones = ({
    values,
    orphanMilestones,
}: {
    values: DeadlineMilestoneValues;
    orphanMilestones: CycleMilestone[];
}): OrphanMilestoneDefinition[] =>
    ORPHAN_MILESTONE_DEFINITIONS.filter((milestone) => {
        const existing = orphanMilestones.find(
            (row) => row.milestoneType === milestone.milestoneType,
        );
        const serverScheduledAt = existing?.scheduledAt
            ? new Date(existing.scheduledAt)
            : undefined;

        return !sameInstant(values[milestone.id]?.scheduledAt, serverScheduledAt);
    });

const getChangedLaunchTypes = ({
    values,
    checkedTypes,
    remindersById,
    cycleEvaluationQueryByType,
}: {
    values: DeadlineMilestoneValues;
    checkedTypes: EvaluationTypeValue[];
    remindersById: RemindersById;
    cycleEvaluationQueryByType: Record<
        EvaluationTypeValue,
        ReturnType<typeof useGetCycleEvaluation>
    >;
}): EvaluationTypeValue[] =>
    checkedTypes.filter((type) => {
        const definition = LAUNCH_MILESTONE_BY_TYPE[type];
        const schedule = cycleEvaluationQueryByType[type]?.data?.cycleSchedule;

        if (!schedule) {
            return false;
        }

        const value = values[definition.id];
        const serverScheduledAt = schedule.scheduledAt ? new Date(schedule.scheduledAt) : undefined;
        const serverDueAt = serverScheduledAt
            ? deriveDueDate({
                  scheduledAt: serverScheduledAt,
                  daysAllowedToComplete: schedule.daysAllowedToComplete,
              })
            : undefined;

        return (
            !sameInstant(value?.scheduledAt, serverScheduledAt) ||
            !sameInstant(value?.dueAt, serverDueAt) ||
            !sameReminders({
                reminders: remindersById[definition.id],
                scheduleReminders: schedule.scheduleReminders,
            })
        );
    });

// An absent recurrence is the state before the group's own value reaches the field, never a
// gesture asking to clear one: writing it would erase a stored choice nobody touched.
const shouldAutosaveRecurrence = ({
    frequency,
    group,
}: {
    frequency?: FrequencyValue;
    group: CycleEvaluationGroup;
}): boolean => !!frequency && frequency !== group.frequency;

export {
    getChangedOrphanMilestones,
    getChangedLaunchTypes,
    sameReminders,
    shouldAutosaveRecurrence,
};
